/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  ReactFlow,
  Node,
  NodeChange,
  EdgeChange,
  Connection,
  Background,
  BackgroundVariant,
  useReactFlow,
  OnConnectStartParams,
  Controls as FlowControls,
  useOnViewportChange,
  Viewport,
  Edge,
  HandleType,
  EdgeLabelRenderer,
} from 'reactflow';

import { v4 as uuidv4 } from 'uuid';
import { useSnackbar } from 'notistack';
import { Connector, ContextMenu, AnchorType, AddCommand, CreateNode, FullEdge, SourceAnchorType } from '../';
import { Command, State, flowActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { findAnchor, getFullEdges } from '@common/utils/flow';
import {
  config,
  ModNodeState,
  ModuleParameter,
  NodeFieldType,
  NodeInputType,
  nodeTypes,
  pairedNodes,
} from '../Nodes/index';
import { Menu } from '../Menu';
import { tabActions } from '@store/slices/tabs';
import { CreateWorkflow } from '@components/Modals';
import { SelectTemplate } from '@components/Modals/SelectTemplate/SelectTemplate';
import { SelectNewWorkflow } from '@components/Modals/SelectNewWorkflow';
import { CanvasWidget } from './Widget';
import { useModules } from '@common/hooks/useModules';
import { useNodeData } from '@common/hooks/useNodeData';
import { useWorkflow } from '@common/hooks/useWorkflow';
import { useParams } from 'react-router-dom';
import { ClearOutlined } from '@mui/icons-material';
import { Button } from '@common/Button';
import { SaveAsTemplate } from '@components/Modals/Templates/SaveAsTemplate';
import { NodeChangelog } from '@components/Modals/NodeChangelog';

const Container = styled.div`
  width: 100%;
  height: 100%;

  position: relative;

  stroke-linecap: round !important;
  stroke-linejoin: round !important;
`;

const Controls = styled(FlowControls)(({ theme }) => ({
  button: {
    backgroundColor: theme.background.secondary,
    color: theme.text.standard,
    border: `1px solid ${theme.line.light}`,
    path: { fill: 'currentColor' },
    '&:hover': { backgroundColor: theme.background.hover },
  },
}));

const isValidConnection = (connection: Connection, commands: Command[]) => {
  const { sourceHandle, targetHandle } = connection;

  const source_anchor = findAnchor(sourceHandle as string, commands);
  const target_anchor = findAnchor(targetHandle as string, commands);

  if (!source_anchor || !target_anchor) return true;

  if (source_anchor.variant === 'data' && target_anchor.variant === 'data') return true;
  if (source_anchor?.variant?.includes('execution') && target_anchor?.variant?.includes('execution')) return true;
  if (source_anchor.variant === 'plugin' && target_anchor.variant === 'plugin') return true;
  if (source_anchor.variant === 'condition' && target_anchor.variant === 'condition') return true;

  return false;
};

const getEdgeStroke = (variant: string | null | undefined, theme: string) => {
  if (!variant) {
    if (theme === 'light') return '#000000';
    return '#ffffff';
  }

  if (variant.includes('execution')) {
    if (theme === 'light') return '#000000';
    if (theme === 'dark') return '#ffffff';
  }

  if (variant === 'data') {
    if (theme === 'light') return '#3434EF';
    if (theme === 'dark') return '#5858FF';
  }

  if (variant === 'plugin') return '#ff7442ae';

  return undefined;
};

const getEdgeStrokeDasharray = (variant: string | null | undefined) => {
  if (!variant) return;

  if (variant === 'data') return '5,5';
  if (variant === 'conditional_execution') return '12,12';
  return undefined;
};

const getNodeWidth = (type: string) => {
  if (type === 'Branch') return '320px';
  if (type === 'RecordingStart') return '320px';
  if (type === 'Switch') return '360px';
  if (type === 'FormatJSON') return '240px';
  return '300px';
};

interface Props {
  abortControllerRef: React.MutableRefObject<AbortController | null>;
}

export const Canvas: React.FC<Props> = ({ abortControllerRef }) => {
  const dispatch = useDispatch();
  const flowRef = useRef<HTMLDivElement>(null);
  const connecting = useRef<OnConnectStartParams | null>(null);
  const theme = useSelector((state: State) => state.flow.theme);
  const edgeUpdateSuccessful = useRef(true);

  const { project } = useReactFlow();
  const { enqueueSnackbar } = useSnackbar();
  const { modules } = useModules();
  const { refreshModules } = useWorkflow();
  const { service_id } = useParams();
  const { getWebhookDataAnchor, getWebhookId, hasDuplicateNodeType } = useNodeData();

  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);
  const [connector, setConnector] = useState<Connector>();

  const [connectionVariant, setConnectionVariant] = useState<string | null>(null);
  const [connectionColor, setConnectionColor] = useState(theme === 'dark' ? '#ffffff' : '#000000');
  const [connectionStrokeWidth, setConnectionStrokeWidth] = useState(2);

  const active = useSelector((state: State) => state.tabs.active);
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const edges = useSelector((state: State) => state.tabs.active.edges);
  const selectedEdge = useSelector((state: State) => state.tabs.active.selectedEdge);
  const full_edges = getFullEdges(edges, commands);

  const trace = useSelector((state: State) => state.tabs.active.run);
  const traceRef = useRef(trace);

  traceRef.current = trace;

  const isTraceActive = traceRef.current?.running || traceRef.current?.id;

  const flowModalOpen = useSelector((state: State) => state.flow.modal) !== null;
  const closeTabModal = useSelector((state: State) => state.flow.closeTab);

  const dataPrefixes = useSelector((state: State) => state.tabs.active.flow?.data_prefixes) || {};
  const nodeIndex = useSelector((state: State) => state.tabs.active.nodeIndex);
  const edgeIndex = useSelector((state: State) => state.tabs.active.edgeIndex);
  const show_data = useSelector((state: State) => state.tabs.active.show_data);
  const show_execution = useSelector((state: State) => state.tabs.active.show_execution);
  const show_tools = useSelector((state: State) => state.tabs.active.show_tools);

  const onNodesChange = useCallback((changes: NodeChange[]) => dispatch(tabActions.onNodeChange(changes)), []);
  const onEdgesChange = useCallback((changes: EdgeChange[]) => dispatch(tabActions.onEdgeChange(changes)), []);

  const nodeMenuDisabled = Boolean(flowModalOpen || closeTabModal);

  const triggerNode = commands.find((command) => command.config.trigger);
  const isInboundCallTrigger = triggerNode?.config?.command_id === 'call.initiated';
  const isModuleWorkflow = triggerNode?.config?.command_id === 'workflow.module';

  const ConnectionLine = ({ fromX, fromY, toX, toY, inputVariant }: any) => {
    // Calculate the midpoint for both X and Y
    const midX = (fromX + toX) / 2;
    const midY = (fromY + toY) / 2;

    const variant = inputVariant || connectionVariant;

    const strokeColor = getEdgeStroke(variant, theme);
    const strokeDasharray = getEdgeStrokeDasharray(variant);
    const strokeWidth = variant === 'plugin' ? 1 : 2;

    // Set control points closer to the midpoint for pronounced central curvature
    const controlX1 = fromX + (midX - fromX) * 0.9; // Closer to the midpoint
    const controlX2 = toX - (toX - midX) * 0.9; // Closer to the midpoint

    // Determine the vertical offset based on the direction of the connection
    const offsetY = Math.abs(toY - fromY) * 0.55; // Large offset for pronounced curvature
    let controlY1, controlY2;
    if (toY > fromY) {
      // Curve goes upward
      controlY1 = midY - offsetY; // Control point above the line
      controlY2 = midY + offsetY; // Control point below the line
    } else {
      // Curve goes downward
      controlY1 = midY + offsetY; // Control point below the line
      controlY2 = midY - offsetY; // Control point above the line
    }

    return (
      <g>
        <path
          fill='none'
          strokeWidth={strokeWidth}
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeDasharray={strokeDasharray}
          stroke={strokeColor}
          d={`M${fromX},${fromY} C ${controlX1},${controlY1} ${controlX2},${controlY2} ${toX},${toY}`}
        />
      </g>
    );
  };

  const SelectedEdge = ({ id, sourceX, sourceY, targetX, targetY }: any) => {
    const fullEdge = full_edges.find((full) => full.edge.id === id);

    const connectionEdge = ConnectionLine({
      fromX: sourceX,
      fromY: sourceY,
      toX: targetX,
      toY: targetY,
      inputVariant: fullEdge?.source_anchor?.variant,
    });

    // Calculate the midpoint
    const midX = (sourceX + targetX) / 2;
    const midY = (sourceY + targetY) / 2;

    const onRemoveEdge = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      dispatch(tabActions.setSelectedNode(null));
      dispatch(tabActions.removeEdgeById({ edgeId: id }));
    };

    return (
      <>
        {connectionEdge}
        <EdgeLabelRenderer>
          <Button
            customColor='red'
            sx={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${midX}px, ${midY}px)`,
              pointerEvents: 'all',
              zIndex: 1000,
            }}
            pill
            className='nodrag nopan'
            onClick={onRemoveEdge}
          >
            <ClearOutlined />
          </Button>
        </EdgeLabelRenderer>
      </>
    );
  };

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdateEnd = useCallback((_: any, edge: Edge) => {
    if (!edgeUpdateSuccessful.current) {
      dispatch(tabActions.removeEdgeById({ edgeId: edge.id }));
    }

    edgeUpdateSuccessful.current = true;
  }, []);

  const onEdgeUpdate = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      edgeUpdateSuccessful.current = true;
      if (!isValidConnection(newConnection, commands)) return;

      const source_anchor = findAnchor(newConnection.sourceHandle as string, commands);
      const target_anchor = findAnchor(newConnection.targetHandle as string, commands);

      const updatedEdge: FullEdge = {
        edge: {
          id: uuidv4(),
          source: newConnection.source as string,
          target: newConnection.target as string,
          updatable: true,
          sourceHandle: newConnection.sourceHandle,
          targetHandle: newConnection.targetHandle,
          style: {
            stroke: getEdgeStroke(source_anchor?.variant, theme),
            strokeWidth: source_anchor?.variant === 'plugin' ? 1 : 2,
            strokeDasharray: source_anchor?.variant === 'conditional_execution' ? '12,12' : undefined,
          },
          animated: source_anchor?.variant === 'data',
        },
        source_anchor: source_anchor as AnchorType,
        target_anchor: target_anchor as AnchorType,
      };

      dispatch(tabActions.removeEdgeConnections({ node_id: oldEdge.target, handle: oldEdge.targetHandle as string }));
      dispatch(tabActions.updateConnection({ oldEdge, newConnection, updatedEdge }));
    },
    [commands]
  );

  const onEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      const stroke = theme === 'dark' ? '#ffffff4a' : '#00000016';
      const selectedEdge = edge;

      dispatch(tabActions.selectEdge({ edges, selectedEdge, stroke }));
    },
    [commands]
  );

  const addNode = ({ type, position, method, connectData, moduleId = null }: CreateNode) => {
    if (!flowRef.current) return;

    const { command, pair, connection, dataConnection, callControlId } = buildCommand({
      type,
      position,
      addPair: false,
      method,
      moduleId,
    });

    const callId =
      dataConnection && connectData && callControlId && command?.state?.call_control_id?.value === ''
        ? callControlId
        : undefined;

    // add the node to the graph (and pair node if applicable)
    dispatch(tabActions.addCommand({ command, pair, callId }));

    // If edge was dragged from an existing node and a new node was added
    if (connection) {
      dispatch(tabActions.addEdge(connection));
      setConnector(null);
    }

    if (dataConnection && connectData) {
      dispatch(tabActions.addEdge(dataConnection));
    }

    // refresh the module output data when a mod node is added
    if (command?.config?.command_id === 'node.module')
      try {
        refreshModules({ service_id, workflow_id: command?.state?.moduleId });
      } catch (e) {}
  };

  const buildCommand = ({ type, position, addPair = false, method, moduleId }: CreateNode): AddCommand => {
    if (!flowRef.current)
      return { command: null, pair: null, connection: null, dataConnection: null, callControlId: undefined };

    const { top, left } = flowRef.current.getBoundingClientRect();

    const node_id = nodeIndex.toString();
    const node_config = config[type as string];
    const pair_config = config[pairedNodes[type as string]];
    const x = position.x;
    const y = position.y;

    const node = {
      id: node_id,
      type: type,
      data: { sources: [], targets: [] },
      style: {
        width: getNodeWidth(type),
        borderRadius: '10px !important',
      },
      selected: true,
      position: project({ x: x - left, y: y - top }),
    };

    const command: Command = {
      node,
      connections: [],
      fields: node_config.fields,
      state: {
        ...node_config.state,
        method: method ? method : node_config?.state?.method,
      },
      config: { ...node_config.config, moduleId },
      output: node_config.output,
    };

    const mod = modules.find((item) => item.flow_id === moduleId);

    if (moduleId && mod) {
      const modParams = mod.parameters.find((param: any) => param.id === 'parameters')?.value;
      command.state.parameters = modParams;

      const fields = modParams.map((param: ModuleParameter) => ({
        id: param.id,
        tab: '1',
        type: NodeFieldType.InputExpression,
        value: '',
        label: param.name,
        description: param.description,
        required: param.required,
        placeholder: param.description,
      }));

      const modState: ModNodeState = { moduleId };

      modParams.forEach((param: ModuleParameter) => {
        modState[param.id] = {
          type: NodeInputType.Expression,
          value: '',
        };
      });

      command.fields = command.fields.concat(fields);
      command.state = { ...command.state, ...modState };
    }

    if (method) command.state.method = method as 'get' | 'post' | 'patch' | 'delete' | 'put';

    // if a node is added that has a pair, we will add the pair node as well
    const pair_node = {
      ...node,
      id: (Number.parseInt(node_id) + 1).toString(),
      type: pairedNodes[type as string],
      position: { x: node.position.x + 900, y: node.position.y },
      selected: false,
    };

    const command_pair: Command = {
      node: pair_node,
      connections: [],
      fields: pair_config?.fields,
      state: pair_config?.state,
      config: pair_config?.config ? { ...pair_config.config, moduleId } : undefined,
      output: pair_config?.output,
    };

    /* if this node was created because an anchor was dragged to open the context menu,
      we need to setup the connection between the node that was dragged and the new node placed */

    if (connector) {
      const { edge } = connector;

      const webhookCommand = commands.find((command) => command.node.id === edge.source);
      const webhookDataAnchor = getWebhookDataAnchor(webhookCommand, edge.sourceHandle);

      const targetNodeExecution = node_config.config.anchors.find(
        (a: AnchorType) => a.type === 'target' && a.variant.includes('execution')
      );

      const targetNodeData = node_config.config.anchors.find(
        (a: AnchorType) => a.type === 'target' && a.variant.includes('data')
      );

      const targetExecutionHandle = targetNodeExecution?.id;
      const targetDataHandle = targetNodeData?.id;

      const source_anchor = findAnchor(edge.sourceHandle as string, commands);
      const sourceCommand = commands.find((command) => command.node.id === edge.source);
      const sourceConfig = sourceCommand?.config?.identifier ? config?.[sourceCommand?.config?.identifier] : undefined;

      const sourceDataAnchor =
        sourceCommand?.config.command_id === 'logic.switch'
          ? {
              label: 'Data',
              id: 'switch.output_data',
              type: 'source' as HandleType,
              variant: 'data',
            }
          : sourceConfig?.config?.anchors?.find(
              (anchor: AnchorType) => anchor.variant === 'data' && anchor.type === 'source'
            );

      const sourceData = webhookDataAnchor || sourceDataAnchor;
      const targetExecutionAnchor = findAnchor(targetExecutionHandle as string, commands);
      const targetDataAnchor = findAnchor(targetDataHandle as string, commands);

      const connection: FullEdge = {
        edge: {
          id: edge.id,
          sourceHandle: edge.sourceHandle,
          source: edge.source,
          target: edge.target,
          updatable: true,
          targetHandle: targetExecutionHandle,
          animated: source_anchor?.variant === 'data',
          style: {
            stroke: getEdgeStroke(source_anchor?.variant, theme),
            strokeWidth: source_anchor?.variant === 'plugin' ? 1 : 2,
            strokeDasharray: source_anchor?.variant === 'conditional_execution' ? '12,12' : undefined,
          },
        },
        source_anchor: source_anchor as AnchorType,
        target_anchor: targetExecutionAnchor as AnchorType,
      };

      const dataConnection: FullEdge = {
        edge: {
          id: uuidv4(),
          sourceHandle: sourceData?.id,
          source: edge.source,
          target: edge.target,
          updatable: true,
          targetHandle: targetDataHandle,
          // label: '3 items',
          animated: true,
          style: {
            stroke: getEdgeStroke('data', theme),
            strokeWidth: 2,
            strokeDasharray: getEdgeStrokeDasharray(sourceData?.variant),
          },
        },
        source_anchor: sourceData as AnchorType,
        target_anchor: targetDataAnchor as AnchorType,
      };

      const webhookId = sourceCommand ? getWebhookId(sourceCommand, source_anchor) : undefined;
      const targetEmptyCallId =
        Boolean(command?.state?.call_control_id) && command?.state?.call_control_id?.value === '';
      const callControlId = webhookId && targetEmptyCallId ? `{{ ${webhookId}.call_control_id }}` : undefined;

      const hasSourceDataAnchor = sourceCommand?.config.anchors.some(
        (anchor) => anchor?.variant === 'data' && anchor?.type === 'source'
      );

      const isHttpDataDisabled = sourceCommand?.config.command_id === 'http.client' && !sourceCommand?.output?.dynamic;
      const isDataPrefixEnabled = Boolean(dataPrefixes?.[sourceCommand?.node?.id || '']);
      const isDataConnectable = hasSourceDataAnchor && !isHttpDataDisabled && !isDataPrefixEnabled;

      if (pair_config)
        return {
          command,
          pair: addPair ? command_pair : null,
          connection,
          dataConnection: isDataConnectable ? dataConnection : null,
          callControlId,
        };

      return {
        command,
        pair: null,
        connection,
        dataConnection: isDataConnectable ? dataConnection : null,
        callControlId,
      };
    }

    if (pair_config)
      return {
        command,
        pair: addPair ? command_pair : null,
        connection: null,
        dataConnection: null,
        callControlId: undefined,
      };

    return { command, pair: null, connection: null, dataConnection: null, callControlId: undefined };
  };

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, type?: string, isEdgeDrag = true) => {
      event.preventDefault();

      if (nodeMenuDisabled || traceRef?.current?.running || traceRef?.current?.id) {
        setContextMenu(null);
        setConnector(null);
        return;
      }

      if (contextMenu !== null) {
        setContextMenu(null);
        setConnector(null);
      }

      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        type: type || 'right-click',
        isEdgeDrag,
      });
    },
    [nodeMenuDisabled, commands]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const source_anchor = findAnchor(connection.sourceHandle as string, commands);
      const target_anchor = findAnchor(connection.targetHandle as string, commands);

      // return if the connection is invalid, e.g. data -> execution
      if (!isValidConnection(connection, commands)) return;

      const edge: FullEdge = {
        edge: {
          id: uuidv4(),
          source: connection.source as string,
          target: connection.target as string,
          updatable: true,
          sourceHandle: connection.sourceHandle,
          targetHandle: connection.targetHandle,
          style: {
            stroke: getEdgeStroke(source_anchor?.variant, theme),
            strokeWidth: source_anchor?.variant === 'plugin' ? 1 : 2,
            strokeDasharray: source_anchor?.variant === 'conditional_execution' ? '12,12' : undefined,
          },
          animated: source_anchor?.variant === 'data',
        },
        source_anchor: source_anchor as AnchorType,
        target_anchor: target_anchor as AnchorType,
      };

      const target_node_id = connection.target;
      const sourceCommand = commands.find((c) => c.node.id === connection.source);

      const targetCommand = commands.find((c) => c.node.id === target_node_id);
      const targetConnections = targetCommand?.connections.filter(
        (c) =>
          c.edge.target === target_node_id &&
          (c.target_anchor?.variant === 'data' || c.target_anchor?.variant === 'condition')
      );

      const targetConnectionCommands = targetConnections?.map((c) =>
        commands.find((cmd) => cmd.node.id === c.edge.source)
      );

      // used to check if two webhook anchors of the same type are connected from different nodes
      const sourceAnchors = targetConnections
        ?.map((connection) => connection.source_anchor)
        .concat({ ...connection, ...source_anchor }) as SourceAnchorType[];

      const allTargets = targetConnectionCommands?.concat(sourceCommand);
      const isDuplicate = hasDuplicateNodeType(allTargets as Command[], sourceAnchors);

      const blockedSources = ['http.response', 'logic.switch', 'logic.branch'];
      const isSourceBranch = blockedSources.includes(sourceCommand?.config.command_id || '');
      const isTargetResponseNode = targetCommand?.config.command_id === 'http.response';

      const isBlockedResponse = isDuplicate && isModuleWorkflow && isTargetResponseNode;
      const isBlockedLogicResponse = isModuleWorkflow && isTargetResponseNode && isSourceBranch;

      if (isBlockedResponse || isBlockedLogicResponse) {
        dispatch(tabActions.setSelectedNode(target_node_id));
        enqueueSnackbar(
          "Connecting data outputs from nodes with the same structure to a Response node is not supported inside modules. Dynamic variables rely on unique node ID's, which will conflict when the module is used in another workflow, leading to incorrect data mapping.",
          {
            variant: 'error',
            autoHideDuration: 20000,
          }
        );

        return;
      }

      if (isDuplicate) {
        console.log(`Duplicate connection on Node ${target_node_id} `);
        dispatch(tabActions.setSelectedNode(target_node_id));
        enqueueSnackbar(
          'You connected the data output from two nodes of the same type. You will need to preface all variables in the connected node with the node_id to distinguish between data from each source.',
          {
            variant: 'info',
            autoHideDuration: 10000,
          }
        );
      }

      const webhookId = sourceCommand ? getWebhookId(sourceCommand, source_anchor) : undefined;
      const callControlId = webhookId ? `{{ ${webhookId}.call_control_id }}` : undefined;
      const targetEmptyCallId =
        Boolean(targetCommand?.state?.call_control_id) && targetCommand?.state?.call_control_id?.value === '';

      if (targetEmptyCallId && callControlId && source_anchor.variant === 'data') {
        dispatch(
          tabActions.nodeInputUpdate({
            nodeId: targetCommand?.node.id,
            fieldId: 'call_control_id',
            fieldName: 'value',
            value: callControlId,
          })
        );
      }

      return dispatch(tabActions.addEdge(edge));
    },
    [dispatch, commands, theme]
  );

  const onConnectStart = useCallback(
    (_: any, params: OnConnectStartParams) => {
      dispatch(tabActions.setConnecting(true));
      setConnector(null);

      const { handleType, handleId } = params;

      const anchor = findAnchor(handleId as string, commands);

      setConnectionVariant(anchor?.variant || null);

      const stroke = getEdgeStroke(anchor?.variant, theme);
      if (stroke) {
        setConnectionColor(stroke);
        setConnectionStrokeWidth(anchor?.variant === 'plugin' ? 1 : 2);
      }

      if (!anchor?.variant.includes('execution') || handleType === 'target') return;

      connecting.current = params;
    },
    [theme, commands]
  );

  const onConnectEnd = useCallback(
    (event: any) => {
      dispatch(tabActions.setConnecting(false));

      const target = event.target as Element;
      const isTargetInCanvas = target.classList.contains('react-flow__pane');
      const isValidNode = connecting?.current?.nodeId;

      // was mouse dragged to a point on the canvas
      if (isTargetInCanvas && isValidNode) {
        const { top, left } = flowRef?.current?.getBoundingClientRect() as DOMRect;

        const edge = {
          id: edgeIndex.toString(),
          sourceHandle: connecting.current?.handleId || '0',
          source: connecting.current?.nodeId || '0',
          target: nodeIndex.toString(),
        };

        // only setup connector if the node has a source data anchor
        setConnector({
          edge,
          node: {
            id: nodeIndex.toString(),
            selected: true,
            data: { sources: [], targets: [] },
            position: project({ x: event.clientX - left, y: event.clientY - top }),
          },
        });

        handleContextMenu(event as unknown as React.MouseEvent, 'edge_add', false);
      }

      connecting.current = null;
    },
    [edgeIndex, nodeIndex, project]
  );

  const selectNode = useCallback((e: React.MouseEvent, node: Node) => {
    dispatch(tabActions.setSelectedNode(node?.id));
    dispatch(flowActions.setTestExecution(null));
  }, []);

  useEffect(() => {
    const lightHasWrongEdge = edges.some((edge) => edge?.style?.stroke === '#ffffff');
    const darkHasWrongEdge = edges.some((edge) => edge?.style?.stroke === '#000000');

    if (theme === 'light' && lightHasWrongEdge)
      dispatch(tabActions.refreshExecutionEdgeColor({ wrongColor: '#ffffff', color: '#000000' }));

    if (theme === 'dark' && darkHasWrongEdge)
      dispatch(tabActions.refreshExecutionEdgeColor({ wrongColor: '#000000', color: '#ffffff' }));
  }, [theme]);

  useOnViewportChange({
    onEnd: (viewport: Viewport) => dispatch(tabActions.setViewport(viewport)),
  });

  const unselectNode = useCallback(() => dispatch(tabActions.setSelectedNode(null)), [dispatch]);
  const checkedEdges = [
    show_data ? 'data' : null,
    show_execution ? 'execution' : null,
    show_execution ? 'webhook_execution' : null,
    show_execution ? 'conditional_execution' : null,
    show_tools ? 'plugin' : null,
  ].filter((x) => x);

  /**
   * This is important.
   * When we create a new condition in a switch node, we need to add a new anchor
   * The canvas wont know about this unless we update the key
   * If we dont update the key, you will see the anchor show but you wont be able to drag edges from it
   */

  const activeConditionGroups = active.commands.filter((item) => item.state?.condition_groups?.find((item) => item.id));
  const activeConditionGroup = activeConditionGroups[activeConditionGroups.length - 1];
  const activeConditionGroupId = activeConditionGroup?.state?.condition_groups?.find((item) => item.id)?.id;

  return (
    <Container onContextMenu={handleContextMenu} style={{ cursor: 'context-menu' }} ref={flowRef}>
      <ReactFlow
        nodes={commands?.map((command) => command.node)}
        edges={full_edges
          .filter((edge) => checkedEdges.includes(edge.source_anchor?.variant))
          .map((full) => {
            if (full.edge.id === selectedEdge?.id) return { ...full.edge, type: 'selectedEdge' };
            return full.edge;
          })}
        onNodesChange={isTraceActive ? undefined : onNodesChange}
        onEdgeClick={isTraceActive ? undefined : onEdgeClick}
        onEdgeUpdate={isTraceActive ? undefined : onEdgeUpdate}
        onEdgeUpdateStart={isTraceActive ? undefined : onEdgeUpdateStart}
        onEdgeUpdateEnd={isTraceActive ? undefined : onEdgeUpdateEnd}
        onEdgesChange={isTraceActive ? undefined : onEdgesChange}
        key={`${active.flow_id}-${activeConditionGroupId || 'default'}`}
        nodesDraggable={!isTraceActive}
        nodesConnectable={!isTraceActive}
        nodesFocusable={!isTraceActive}
        edgesFocusable={!isTraceActive}
        elementsSelectable={!isTraceActive}
        className='nokey'
        nodeTypes={nodeTypes}
        edgeTypes={{ selectedEdge: SelectedEdge }}
        connectionLineComponent={ConnectionLine}
        connectionLineStyle={{
          stroke: connectionColor,
          strokeWidth: connectionStrokeWidth,
        }}
        onConnect={isTraceActive ? undefined : onConnect}
        onConnectStart={isTraceActive ? undefined : onConnectStart}
        onConnectEnd={isTraceActive ? undefined : onConnectEnd}
        fitView={!(active?.viewport.x !== 0 && active?.viewport?.y !== 0)}
        deleteKeyCode={null}
        onNodeClick={isTraceActive ? undefined : selectNode}
        onNodeDrag={isTraceActive ? undefined : selectNode}
        onNodeDragStart={isTraceActive ? undefined : selectNode}
        onPaneClick={isTraceActive ? undefined : unselectNode}
        snapToGrid
        edgeUpdaterRadius={12}
        snapGrid={[20, 20]}
        minZoom={0.1}
        defaultViewport={active?.viewport.x !== 0 && active?.viewport?.y !== 0 ? active.viewport : undefined}
      >
        <Controls position='bottom-right' />
        <Background variant={BackgroundVariant.Dots} color={theme === 'light' ? '#b8b2b2' : '#494e57'} size={1} />
      </ReactFlow>
      <Menu
        nodes={commands.map((command) => command.node)}
        contextMenu={contextMenu}
        setConnector={setConnector}
        setContextMenu={setContextMenu}
        addNode={addNode}
      />

      {
        /**
         * We can't show the "Run Workflow" widget for inbound calls
         * This is because the voice application data is not pulled until sidebar is opended
         * RTC client is also setup when sidebar is opened which allows calls to be made
         * @TODO Find a better solution for setting up inbound calls
         */

        !isInboundCallTrigger && <CanvasWidget abortControllerRef={abortControllerRef} />
      }
      <SelectNewWorkflow />
      <SelectTemplate />
      <NodeChangelog />
      <CreateWorkflow addNode={addNode} />
      <SaveAsTemplate />
    </Container>
  );
};
